import _ from 'lodash'
import api from '../../api/assets'
import { generateMetadata } from '../../helpers/assets'
import EventBus from '../../helpers/event-bus'

export default {
  namespaced: true,
  state: {
    source: null,
    ready: false,
    loaded: false,
    selectedId: null,
    options: { },
    mode: null,
    files: { },
    fileList: [],
    metadata: { },
    filesLoading: { },
    loadTranslations: false,
    translatedFiles: { }
  },
  getters: {
    fileSet: ({ files, fileList }) => fileList.map(id => files[id]),
    selectedFile: ({ files, selectedId }) => files[selectedId],
    selectedMetadata: ({ metadata, selectedId }) => _.get(metadata, selectedId, { }),
    fileMap: ({ files }) => _.keyBy(files, 'url'),
    selectedTranslatedFile: ({ translatedFiles, selectedId }) => _.find(translatedFiles, { parent_media_id: selectedId }),
    selectedTranslatedMetadata: ({ metadata, selectedId, translatedFiles }) => _.get(metadata, _.find(translatedFiles, { parent_media_id: selectedId })?.id, { })
  },
  mutations: {
    setSource (state, value) {
      state.source = value
    },
    setFiles (state, value) {
      state.files = _.isArray(value) ? _.keyBy(value, 'id') : value
      state.fileList = _.map(value, 'id')
    },
    addFile (state, value) {
      state.files[parseInt(value.id, 10)] = value
    
      if (state.fileList.indexOf(value.id) === -1) {
        state.fileList.push(value.id)
      }
    },
    updateFile (state, value) {
      state.files[parseInt(value.id, 10)] = value
    },
    removeFile (state, id) {
      const index = state.fileList.indexOf(id)
      if (index >= 0) {
        state.fileList.splice(index, 1)
      }
    
      delete state.files[id];
      delete state.metadata[id];
    },
    updateMetadata (state, value) {
      state.metadata[value.id] = value;
    },
    setSelectedId (state, value) {
      state.selectedId = value
    },
    setOptions (state, value) {
      state.options = value
      state.selectedId = null
    },
    setMode (state, value) {
      state.mode = value
    },
    setReady (state, value) {
      state.ready = value
    },
    setLoaded (state, value) {
      state.loaded = value
    },
    setFileLoading (state, id) {
      state.filesLoading[id] = true
    },
    setFileLoaded (state, id) {
      state.filesLoading[id] = false
    },
    setLoadTranslations (state, value) {
      state.loadTranslations = value
    },
    setTranslatedFiles (state, value) {
      state.translatedFiles = _.isArray(value) ? _.keyBy(value, 'id') : value
    },
    removeTranslatedFile (state, id) {
      delete state.translatedFiles[id]
      delete state.metadata[id]
    },
    updateTranslatedFile (state, value) {
      state.translatedFiles[parseInt(value.id, 10)] = value
    }
  },
  actions: {
    async update ({ state, commit }, source) {
      if (source) {
        commit('setSource', source)
      }

      const result = await api.all(state.source)
      if (result) {
        commit('setFiles', result)
        commit('setLoaded', true)
        EventBus.$emit('assets:updated')
      }
    },
    async selectFile ({ state, commit, dispatch, getters }, id) {
      commit('setSelectedId', id)

      // build initial metadata
      if (!state.metadata[id]) {
        await dispatch('refreshMetadata', id)
      }

      history.replaceState({ id }, '', `#${id}`)

      // get latest from server
      await dispatch('getFile', id)

      if (state.loadTranslations)
      {
        const selectedTranslatedId = getters.selectedTranslatedFile?.id

        if (selectedTranslatedId) {
          // get latest from server
          await dispatch('getTranslatedFile', selectedTranslatedId)
        }
      }
    },
    selectByFilename ({ getters, dispatch }, url) {
      const file = _.find(getters.fileSet, { url })
      // console.log(getters.fileSet, url, file)
      if (file) {
        dispatch('selectFile', file.id)
      }
    },
    async refreshMetadata ({ state, commit, getters }, id) {
      // find in files or in translatedFile
      const file = state.files[id] ?? state.translatedFiles[id] ?? null
      if (file) {
        const metadata = await generateMetadata(file)
        commit('updateMetadata', metadata)

        if (state.loadTranslations)
        {
          const translatedFile = getters.selectedTranslatedFile

          if (translatedFile && translatedFile != file) {
            const metadata = await generateMetadata(translatedFile)
            commit('updateMetadata', metadata)
          }
        }
      } else {
        console.warn('File not found with ID:', id)
      }
    },
    async getFile ({ state, commit, dispatch }, opts) {
      let id
      if ('object' === typeof opts) {
        id = opts.id

        // check if pull cached data
        if (opts.useCache && state.files[id]) {
          return state.files[id]
        }
      } else {
        id = opts
      }

      commit('setFileLoading', id)
      const result = await api.get(id)
      if (result) {
        commit('updateFile', result)
        await dispatch('refreshMetadata', id)
      }
      commit('setFileLoaded', id)

      return state.files[id]
    },
    async updateFile ({ commit }, data) {
      commit('setFileLoading', data.id)
      try {
        const result = await api.update(data.id, data)
        if (result) {
          commit('updateFile', result)
        }
        commit('setFileLoaded', data.id)
        return result
      } catch (err) {
        commit('setFileLoaded', data.id)
        throw err // Re-throw to let component handle it
      }
    },
    async deleteFile ({ commit }, id) {
      const result = await api.destroy(id)
      if (result) {
        commit('removeFile', id)
      }
    },
    async updateTranslatedFile ({ commit }, data) {
      // commit('setFileLoading', data.id)
      const result = await api.update(data.id, data)
      if (result) {
        commit('updateTranslatedFile', result)
      }
      // commit('setFileLoaded', data.id)
    },
    async getTranslatedFile ({ state, commit, dispatch }, opts) {
      let id
      if ('object' === typeof opts) {
        id = opts.id

        // check if pull cached data
        if (opts.useCache && state.translatedFiles[id]) {
          return state.translatedFiles[id]
        }
      } else {
        id = opts
      }

      const result = await api.get(id)
      if (result) {
        commit('updateTranslatedFile', result)
        await dispatch('refreshMetadata', id)
      }

      return state.translatedFiles[id]
    }
  }
}
